import { constructValue } from '@adornis/baseql/entities/construct.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { DateTime } from 'luxon';
import { ChancenRole } from '../../../semantics/ChancenRole.js';
import { Mitarbeiter } from '../entities/Mitarbeiter.js';

/**
 * Fetches all employees with a specific Taetigkeit.
 *
 * @query getEmployeesWithTaetigkeit
 * @param kpfID - The ID of the KPF
 * @param predicateID - The ID of the predicate
 * @param from - The date from which the relationship should be active
 * @returns The employees with the given Taetigkeit
 */
export const getEmployeesWithTaetigkeit = registerQuery({
  type: () => [Mitarbeiter],
  operationName: 'getEmployeesWithTaetigkeit',
  resolve: (kpfID: string, predicateID: string, from: DateTime = DateTime.now()) => {
    return async (gqlFields: BaseQLSelectionSet<Mitarbeiter>) => {
      const predicate = (await loadMongoData(ChancenRole, { _id: predicateID })).at(0);
      if (!predicate) return [];

      const TaetigkeitClass = (await import('../../../semantics/Taetigkeit.js')).Taetigkeit;

      const relationshipsForKPF = await loadMongoData(TaetigkeitClass, {
        objectID: kpfID,
        predicateID: predicate._id,
        ...TaetigkeitClass.isActiveMongoSelector(from),
      });

      return (
        await loadMongoData(
          Mitarbeiter,
          relationshipsForKPF.map(rel => rel.subjectID),
        )
      ).map(user => constructValue(user) as Mitarbeiter);
    };
  },
  options: {},
  params: [
    { type: () => String, name: 'kpfID' },
    { type: () => String, name: 'predicateID' },
    { type: () => DateTime, name: 'from' },
  ],
});
